import { UnknownElementError } from '@/features/legends/editor/legend-document'
import { ClearInventoryItemQuantityMutatorEditorViewModel } from '@/features/legends/effects/mutators/ClearInventoryItemQuantityMutatorEditorViewModel'
import { CopyInventoryItemMutatorEditorViewModel } from '@/features/legends/effects/mutators/CopyInventoryItemMutatorEditorViewModel'
import { InventoryItemMutatorEditorViewModel } from '@/features/legends/effects/mutators/InventoryItemMutatorEditorViewModel'
import { ListMutatorEditorViewModel } from '@/features/legends/effects/mutators/ListMutatorEditorViewModel'
import { ReplaceMutatorEditorViewModel } from '@/features/legends/effects/mutators/ReplaceMutatorEditorViewModel'
import { VaryMutatorEditorViewModel } from '@/features/legends/effects/mutators/VaryMutatorEditorViewModel'
import type { MutatorEditorViewModelOptions } from './MutatorEditorViewModelBase'
import type { Mutator, MutatorType } from './effects'

/**
 * Any mutator editor, narrowed by its `type`.
 */
export type AnyMutatorEditor =
  | ClearInventoryItemQuantityMutatorEditorViewModel
  | CopyInventoryItemMutatorEditorViewModel
  | InventoryItemMutatorEditorViewModel
  | ListMutatorEditorViewModel
  | ReplaceMutatorEditorViewModel
  | VaryMutatorEditorViewModel

/**
 * What the "add mutator" menu needs to know about a mutator type.
 */
export interface MutatorTypeInfo {
  type: MutatorType
  title: string
  description: string
}

/**
 * Mutator types, in the order the "add mutator" menu offers them.
 */
export const mutatorTypes: Record<MutatorType, MutatorTypeInfo> = {
  InventoryItem: {
    type: 'InventoryItem',
    title: 'Update service item quantity',
    description: 'Update the quantity on a service item element.',
  },
  CopyInventoryItem: {
    type: 'CopyInventoryItem',
    title: 'Copy service item quantity',
    description:
      'Copy the quantity of a service item element to the target service item element.',
  },
  ClearInventoryItemQuantity: {
    type: 'ClearInventoryItemQuantity',
    title: 'Clear service item quantity',
    description: 'Clear the quantity of the target service item element.',
  },
  Vary: {
    type: 'Vary',
    title: 'Vary',
    description: 'Increment or decrement a number by some amount.',
  },
  List: {
    type: 'List',
    title: 'List operation',
    description: 'Add or remove item(s) from a List-type variable.',
  },
  Replace: {
    type: 'Replace',
    title: 'Replace',
    description: 'Put a completely new value into a variable.',
  },
}

/**
 * Creates an empty mutator editor of the type.
 *
 * @param type
 * @param opts
 */
export function createMutator(
  type: MutatorType,
  opts: MutatorEditorViewModelOptions,
): AnyMutatorEditor {
  switch (type) {
    case 'InventoryItem':
      return new InventoryItemMutatorEditorViewModel(opts)
    case 'CopyInventoryItem':
      return new CopyInventoryItemMutatorEditorViewModel(opts)
    case 'ClearInventoryItemQuantity':
      return new ClearInventoryItemQuantityMutatorEditorViewModel(opts)
    case 'Vary':
      return new VaryMutatorEditorViewModel(opts)
    case 'List':
      return new ListMutatorEditorViewModel(opts)
    case 'Replace':
      return new ReplaceMutatorEditorViewModel(opts)
  }
}

/**
 * Creates a mutator editor hydrated from the mutator. Throws for a mutator of
 * a type the builder doesn't know, which no editor can show.
 *
 * @param mutator
 * @param opts
 */
export function hydrateMutator(
  mutator: Mutator,
  opts: MutatorEditorViewModelOptions,
): AnyMutatorEditor {
  if (mutator.type === 'Unknown') {
    throw new UnknownElementError('mutator', mutator)
  }
  const editor = createMutator(mutator.type, opts)
  // SAFETY: the editor was created for the mutator's own type.
  ;(editor as { hydrate(mutator: Mutator): void }).hydrate(mutator)
  return editor
}
